import { TCurrencyCode } from "@sembark-travel/money"
import { AsyncSelect } from "@sembark-travel/ui-base"
import { useXHR } from "@sembark-travel/xhr"
import { currenciesXHR, useCurrenciesOfTenant } from "./store"
import {
	InlineSelectInputField,
	SelectInputField,
	useFieldValue,
	MoneyInputField,
} from "@sembark-travel/ui-form"
import { useEffect } from "react"

export function SelectCurrencyFromAll(
	props: Omit<React.ComponentProps<typeof AsyncSelect>, "fetch">
) {
	const xhr = useXHR()
	return (
		<AsyncSelect
			{...props}
			fetch={async (q) => {
				return currenciesXHR(xhr)
					.getAllAvailableCurrencies({ q })
					.then((resp) => resp.data)
			}}
		/>
	)
}

export function SelectPendingPaymentCurrencies({
	hotelIds,
	suppliersIds,
	setDefaultCurrency,
	...rest
}: Omit<React.ComponentProps<typeof AsyncSelect>, "fetch"> & {
	hotelIds?: number[] | string
	suppliersIds?: number[] | string
	setDefaultCurrency?: (currency: TCurrencyCode) => void
}) {
	const xhr = useXHR()
	const supplier_ids = Array.isArray(suppliersIds)
		? suppliersIds
		: [suppliersIds]
	const hotel_ids = Array.isArray(hotelIds) ? hotelIds : [hotelIds]
	return (
		<AsyncSelect
			{...rest}
			fetch={async (q) => {
				return currenciesXHR(xhr)
					.getPendingPaymentCurrencies({ q, hotel_ids, supplier_ids })
					.then((resp) => {
						if (setDefaultCurrency && resp.data.length === 1) {
							setDefaultCurrency(resp.data[0])
						}
						return resp.data
					})
			}}
		/>
	)
}
export function SelectTenantCurrencyInputField({
	clearable,
	disabledCurrencies,
	...props
}: Omit<React.ComponentProps<typeof SelectInputField>, "children"> & {
	clearable?: boolean
	disabledCurrencies?: TCurrencyCode[]
}) {
	const currencies = useCurrenciesOfTenant()
	return (
		<SelectInputField
			disabled={!clearable && currencies.length <= 1}
			{...props}
		>
			{clearable ? <option value="">-</option> : null}
			{currencies.map((c) => (
				<option
					key={c.currency}
					value={c.currency}
					disabled={disabledCurrencies?.includes(c.currency)}
				>
					{c.currency}
				</option>
			))}
		</SelectInputField>
	)
}

export function InlineSelectTenantCurrencyInputField({
	clearable,
	defaultValue,
	disabledCurrencies,
	...props
}: Omit<React.ComponentProps<typeof InlineSelectInputField>, "children"> & {
	clearable?: boolean
	disabledCurrencies?: TCurrencyCode[]
}) {
	const currencies = useCurrenciesOfTenant()
	const { value, onChange } = useFieldValue(props.name)
	useEffect(() => {
		if (!value && defaultValue && !clearable) {
			setTimeout(() => {
				onChange(defaultValue)
			})
		}
	}, [value, defaultValue, clearable, onChange])
	const disabled = !clearable && currencies.length <= 1
	return (
		<InlineSelectInputField
			disabled={disabled}
			{...props}
			noCaret={props.noCaret || disabled}
		>
			{clearable ? <option value="">-</option> : null}
			{currencies.map((c) => (
				<option
					key={c.currency}
					value={c.currency}
					disabled={disabledCurrencies?.includes(c.currency)}
				>
					{c.currency}
				</option>
			))}
		</InlineSelectInputField>
	)
}

export function MoneyInputWithTenantCurrenciesField(
	props: Omit<React.ComponentProps<typeof MoneyInputField>, "currencies">
) {
	const currencies = useCurrenciesOfTenant()
	return (
		<MoneyInputField
			{...props}
			currencies={currencies.map((c) => c.currency)}
		/>
	)
}
